// The frame around every signed-in screen: sidebar, top bar, and the page itself.

import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import logo from "../assets/gum-logo.svg";
import { canDecide, isApprover } from "../lib/leave";
import { titleOf } from "../lib/orgchart";
import { useStore } from "../store/StoreProvider";
import { useCtx } from "../store/useCtx";
import { Avatar } from "../ui/bits";
import { useDialogs } from "../ui/dialogs";
import { Icon } from "../ui/Icon";
import type { IconName } from "../ui/icons";
import { useToast } from "../ui/Toasts";
import { Modal } from "../ui/Modal";
import { PhotoEditor } from "../ui/PhotoEditor";
import { titleFor } from "./routes";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  badge?: number;
}

export function Shell({ onExitDemo }: { onExitDemo?: () => void }) {
  const ctx = useCtx();
  const store = useStore();
  const toast = useToast();
  const { openRequest } = useDialogs();
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const main = useRef<HTMLElement>(null);
  const { me } = ctx;
  const title = titleFor(pathname);
  const page = pathname.split("/")[1] ?? "";

  useEffect(() => {
    document.title = `${title} · Playbrush Holiday`;
  }, [title]);

  // Arriving on a different page: close the phone menu, go to the top and put keyboard focus on the page.
  useEffect(() => {
    setNavOpen(false);
    main.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [page]);

  // The stylesheet shows the phone menu when the body has this class.
  useEffect(() => {
    document.body.classList.toggle("nav-open", navOpen);
    return () => document.body.classList.remove("nav-open");
  }, [navOpen]);

  const pending = ctx.requests.filter((r) => r.status === "pending" && canDecide(me, r, ctx)).length;
  const nav: NavItem[] = [
    { to: "/", label: "Dashboard", icon: "home", end: true },
    { to: "/calendar", label: "Team calendar", icon: "calendar" },
    { to: "/year", label: "My year", icon: "grid" },
    { to: "/org-chart", label: "Org chart", icon: "users" },
    ...(isApprover(me, ctx)
      ? [{ to: "/approvals", label: "Approvals", icon: "check-circle" as const, badge: pending }]
      : []),
    ...(me.role === "hr" ? [{ to: "/admin/people", label: "Admin", icon: "sliders" as const }] : []),
  ];

  const resetDemo = async () => {
    if (store.mode !== "demo" || resetBusy) return;
    setResetBusy(true);
    try {
      await store.resetDemo();
      toast("Demo data reset.");
    } catch {
      toast("Could not reset the demo. Your changes have been kept. Please try again.", "error");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <>
      <a
        className="skip-link"
        href="#main"
        onClick={(e) => {
          // A plain #main link would change the hash, which is what the router reads.
          e.preventDefault();
          main.current?.focus();
        }}
      >
        Skip to main content
      </a>

      {store.mode === "demo" && (
        <div className="demo-bar" role="note">
          <strong>Demo mode</strong> · Sample data. Changes stay in this browser.{" "}
          <button type="button" className="link-btn light" onClick={() => void resetDemo()} disabled={resetBusy}>
            {resetBusy ? "Resetting..." : "Reset demo data"}
          </button>
          {onExitDemo && (
            <>
              {" "}
              ·{" "}
              <button type="button" className="link-btn light" onClick={onExitDemo}>
                Exit demo
              </button>
            </>
          )}
        </div>
      )}

      <div className="layout">
        <aside className="sidebar" id="sidebar" aria-label="Main">
          <div className="brand">
            <img src={logo} alt="GUM" width={72} height={34} />
            <span className="brand-name">Playbrush Holiday</span>
          </div>
          <nav>
            <ul>
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink to={n.to} end={n.end} className="nav-link">
                    <Icon name={n.icon} />
                    <span>{n.label}</span>
                    {!!n.badge && (
                      <span className="badge" aria-label={`${n.badge} waiting`}>
                        {n.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="me">
            <div className="me-row">
              <Avatar name={me.name} photoUrl={me.photoUrl} className="avatar-on-dark" />
              <div>
                <div className="me-name">{me.name}</div>
                <div className="me-role">{titleOf(me)}</div>
              </div>
            </div>
            <button type="button" className="btn-sidebar" onClick={() => setPhotoOpen(true)}>
              <Icon name="users" size={18} />
              Edit profile photo
            </button>
            {photoOpen && (
              <Modal
                title="Your profile photo"
                titleId="photo-title"
                onClose={() => {
                  if (!photoBusy) setPhotoOpen(false);
                }}
              >
                <PhotoEditor userId={me.id} onBusyChange={setPhotoBusy} />
              </Modal>
            )}
            <button type="button" className="btn-sidebar" onClick={() => void store.signOut()}>
              <Icon name="log-out" size={18} />
              {store.mode === "demo" ? "Switch demo user" : "Sign out"}
            </button>
          </div>
        </aside>
        <div className="scrim" onClick={() => setNavOpen(false)} />
        <div className="content">
          <header className="topbar">
            <button
              type="button"
              className="icon-btn menu-btn"
              onClick={() => setNavOpen((open) => !open)}
              aria-label="Open menu"
              aria-controls="sidebar"
              aria-expanded={navOpen}
            >
              <Icon name="menu" />
            </button>
            <h1 id="page-title">{title}</h1>
            <button type="button" className="btn" onClick={() => openRequest()}>
              <Icon name="plus" size={18} />
              <span>Request leave</span>
            </button>
          </header>
          <main id="main" ref={main} tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
